import { toast } from 'sonner';
import { registerActionHandler } from '../action-registry';
import { buildSmallAppUrl } from '../smallapp-url';
import { getSmallAppRestToken } from '../get-smallapp-rest-token';
import { getNested } from '../nested-keys';
import { applyResponseMapping } from '../response-mapping';

/**
 * `resolveDriveUrl` — the OneDrive "paste a URL" action. Resolves a pasted SharePoint/OneDrive
 * URL to a drive item via the onedrive microservice and writes the SAME form keys the
 * `drive-picker` sets, so the pasted-URL path and the picker path produce identical
 * `folder.*` / `profile.*` values (downstream save/sync is unchanged).
 *
 * Runs on ADD (no saved asset): calls `{apiUrl}/resolve-url` directly with the Firebase/
 * share-link REST token (`getSmallAppRestToken`) — NOT the open-api `SMALLAPP_PROXY_ACTION`
 * proxy, which requires a saved asset. Mirrors the drive-picker (which browses on Add with the
 * in-form OAuth token) and `useFetchOptions`.
 *
 * The pasted URL is `values[field.key]`; the delegated MS token travels in the in-form `profile`
 * (the microservice refreshes it server-side on 401). Failure (bad URL / no token / unreachable)
 * only toasts — it does NOT write `folder.*`, so the save is blocked by the required `folder`
 * anchor (see the manifest). Button-triggered (no `runOnSave`): the write must happen before
 * Save, since ConfigEngine builds the save payload from the pre-loop `values` snapshot.
 *
 * `$response.driveType` (parentReference.driveType) is distinct from `itemKind` (folder|file) —
 * `folder.type` / `profile.drive_type` carry the DRIVE type, matching the picker.
 */
// Response → form-key maps, PER app type — each MIRRORS that app's drive-picker
// `drivePickerConfig.setValues`, so the pasted-URL path writes identical config to the picker.
// The resolver returns a SUPERSET (raw Graph driveItem + normalized aliases driveId/itemId/…), so
// both the normalized paths (OneDrive) and the raw `$response.*` paths (Excel/PowerPoint) resolve.
const ONEDRIVE_MAP = {
  'folder.id': '$response.itemId',
  'folder.name': '$response.name',
  'folder.driveId': '$response.driveId',
  'folder.type': '$response.driveType',
  'profile.drive_id': '$response.driveId',
  'profile.drive_type': '$response.driveType',
} as const;

// Excel picks a FILE and stores rich metadata (parentReference/fileSystemInfo/createdBy/size).
const MICROSOFT_EXCEL_MAP = {
  'folder.id': '$response.id',
  'folder.name': '$response.name',
  'folder.parentReference': '$response.parentReference',
  'folder.fileSystemInfo': '$response.fileSystemInfo',
  'folder.createdBy': '$response.createdBy',
  'folder.size': '$response.size',
  'folder.webUrl': '$response.webUrl',
  'profile.drive_id': '$response.parentReference.driveId',
  'profile.drive_type': '$response.parentReference.driveType',
  webLink: '$response.webUrl',
} as const;

const POWERPOINT_MAP = {
  'folder.id': '$response.id',
  'folder.name': '$response.name',
  'folder.driveId': '$response.parentReference.driveId',
  'folder.type': '$response.parentReference.driveType',
  'profile.drive_id': '$response.parentReference.driveId',
  'profile.drive_type': '$response.parentReference.driveType',
  webLink: '$response.webUrl',
} as const;

const RESPONSE_MAPS: Record<string, Record<string, string>> = {
  onedrive: ONEDRIVE_MAP,
  microsoftExcel: MICROSOFT_EXCEL_MAP,
  powerpoint: POWERPOINT_MAP,
};

registerActionHandler(
  'resolveDriveUrl',
  async ({ values, form, appMeta, field, t, apolloClient }) => {
    const apiUrl = appMeta?.apiUrl;
    const url = String(getNested(values, field.key) ?? '').trim();
    if (!url) {
      toast.error(t('Paste a folder or file URL first'));
      return;
    }
    if (!apiUrl) {
      toast.error(t('This app is not configured'));
      return;
    }
    const profile = getNested(values, 'profile');
    const hasToken = !!(
      profile &&
      typeof profile === 'object' &&
      (profile as Record<string, unknown>).access_token
    );
    if (!hasToken) {
      toast.error(t('Connect your Microsoft account first'));
      return;
    }

    // Firebase/share-link REST token the microservice accepts (the open-api proxy 403s + needs a
    // saved asset). Same method the drive-picker / useFetchOptions use.
    const token = await getSmallAppRestToken(apolloClient);

    let response: Response;
    try {
      response = await fetch(buildSmallAppUrl(apiUrl, 'resolve-url'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ url, profile }),
      });
    } catch {
      toast.error(t('Could not reach OneDrive. Please try again.'));
      return;
    }

    if (!response.ok) {
      let message = 'Could not resolve that URL. Check the link and your access.';
      try {
        const err = (await response.json()) as Record<string, unknown>;
        if (typeof err?.message === 'string') message = err.message;
      } catch {
        // non-JSON error body — keep the default message
      }
      toast.error(t(message));
      return;
    }

    const data = (await response.json()) as Record<string, unknown>;
    if (!data || !data.itemId || !data.driveId) {
      toast.error(t('Could not resolve that URL.'));
      return;
    }

    // Enforce the app's accepted kind (the paste-URL equivalent of the picker's `viewType`):
    // OneDrive accepts only folders; Excel/PowerPoint only files. Reject the other kind — toast and
    // do NOT write, so the required `_folderPicker`/`_filePicker` gate keeps the save blocked.
    const expectKind = field.action?.expectKind;
    if (expectKind && data.itemKind && data.itemKind !== expectKind) {
      toast.error(
        t(
          expectKind === 'folder'
            ? 'That link points to a file. Paste a link to a folder.'
            : 'That link points to a folder. Paste a link to a file.',
        ),
      );
      return;
    }

    // Write the picker-identical folder.* / profile.* keys directly (bypasses the
    // onSuccess.setValues field-key validation that would reject undeclared folder.driveId).
    // Pick the per-app map (OneDrive / Excel / PowerPoint); default to OneDrive's.
    const mapping = RESPONSE_MAPS[appMeta?.appType ?? ''] ?? ONEDRIVE_MAP;
    applyResponseMapping(form, mapping, data);
    // Success copy matches what was actually linked — a file (Excel/PowerPoint) vs a folder (OneDrive).
    toast.success(t(data.itemKind === 'file' ? 'File linked' : 'Folder linked'));
  },
);
